/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      instance-initializer — fixed top banner shown while the browser
 *              is offline, driven by the native online/offline events.
 *              Replaces the offline-js vendor lib.
 *   template:  none
 *   key:       initialize(appInstance), bannerText(intl), lookupIntl(owner)
 *   uses:      service:intl (key 'offline_banner.no_connection'), global
 *              window/navigator/document
 *   siblings:  beebee-base/app/styles/custom_new.scss (.bb-offline-banner)
 *   tests:     tests/unit/instance-initializers/offline-banner-test.js
 *   gotchas:   an INSTANCE initializer (not a plain initializer) because the
 *              text needs service:intl. The banner boots before the user's
 *              locale and translations are loaded, so the text is re-read every
 *              time the banner is shown and whenever ember-intl switches the
 *              <html lang> attribute; until the key exists it falls back to
 *              English instead of showing a raw key. One banner per window: a
 *              later app instance (tests, engines) reuses it and takes over the
 *              owner reference, so a destroyed instance's service is never read.
 *              service:intl is looked up LAZILY (only when the banner shows): an
 *              eager lookup at boot instantiated the singleton before rendering
 *              tests could register their intl stub.
 */
const KEY = 'offline_banner.no_connection';
const FALLBACK = 'No internet connection.';
function bannerText(intl) {
  try {
    if (intl && !intl.isDestroyed && !intl.isDestroying && intl.exists(KEY)) {
      return intl.t(KEY);
    }
  } catch {
    // fall through to the English fallback
  }
  return FALLBACK;
}

// Resolve service:intl lazily, and never through a torn-down owner (the banner
// outlives app instances: tests, engines).
function lookupIntl(owner) {
  if (!owner || owner.isDestroyed || owner.isDestroying) return null;
  try {
    return owner.lookup('service:intl') ?? null;
  } catch {
    return null;
  }
}
function createBanner() {
  const el = document.createElement('div');
  el.className = 'bb-offline-banner';
  el.setAttribute('role', 'status');
  document.body.appendChild(el);
  const state = {
    el,
    owner: null
  };
  state.refresh = () => {
    const offline = !navigator.onLine;
    if (offline) {
      el.textContent = bannerText(lookupIntl(state.owner));
    }
    el.classList.toggle('is-visible', offline);
  };
  window.addEventListener('online', state.refresh);
  window.addEventListener('offline', state.refresh);

  // ember-intl sets <html lang> on every setLocale: re-read the text so a
  // banner already visible at boot switches from the fallback to the locale.
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(state.refresh).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang']
    });
  }
  return state;
}
function initialize(appInstance) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  window.__beebeeOfflineBanner ??= createBanner();
  const state = window.__beebeeOfflineBanner;
  // Keep the owner, not the service: looking service:intl up here instantiated
  // the singleton during every app-instance boot, before a rendering test gets
  // to register its intl stub — the stub was then silently ignored and every
  // `intl.t` in those tests hit the real, locale-less ember-intl ("locales is
  // not iterable"). The service is resolved only when the banner is shown.
  state.owner = appInstance;
  state.refresh();
}
var offlineBanner = {
  name: 'offline-banner',
  initialize
};

export { bannerText, offlineBanner as default, initialize, lookupIntl };
//# sourceMappingURL=offline-banner.js.map
